import React, { useCallback, useEffect, useRef, useState } from "react";
import { Button, DatePicker, InputNumber, Select, Table, Tag } from "antd";
import { format } from "date-fns";
import authApi from "../../../api/authApi";
import ExportCsvButton from "../../../components/export-csv-button";

const { RangePicker } = DatePicker;

const PAGE_SIZE = 50;

const TYPE_OPTIONS = [
    { value: "",       label: "Tất cả" },
    { value: "bonus",  label: "Thưởng" },
    { value: "deduct", label: "Trừ tiền" },
];

const columns = [
    {
        title: "ID",
        dataIndex: "bonusId",
        width: 70,
        render: (v) => `#${v}`,
    },
    {
        title: "User",
        key: "user",
        render: (_, row) =>
            row.bonusUser
                ? `${row.bonusUser.name} (#${row.userId})`
                : `#${row.userId}`,
    },
    {
        title: "Loại",
        dataIndex: "bonusCash",
        key: "type",
        width: 110,
        render: (v) =>
            v < 0 ? <Tag color="red">Trừ tiền</Tag> : <Tag color="green">Thưởng</Tag>,
    },
    {
        title: "Số tiền",
        dataIndex: "bonusCash",
        width: 140,
        render: (v) => (
            <span className={v < 0 ? "text-red-500 font-semibold" : "text-green-600 font-semibold"}>
                {v < 0 ? "−" : "+"}
                {Math.abs(v).toLocaleString("vi-VN")}đ
            </span>
        ),
    },
    {
        title: "Admin",
        key: "admin",
        render: (_, row) =>
            row.bonusAdmin
                ? `${row.bonusAdmin.name} (#${row.adminId})`
                : row.adminId ? `#${row.adminId}` : "—",
    },
    {
        // Lý do này user cũng nhìn thấy ở "Lịch sử số dư" → đối chiếu khiếu nại
        // ngay tại đây, khỏi mò notification.
        title: "Lý do",
        dataIndex: "note",
        ellipsis: true,
        render: (v) => v || <span className="text-content-3">—</span>,
    },
    {
        title: "Thời gian",
        dataIndex: "createTime",
        width: 155,
        render: (v) => {
            if (!v) return "—";
            const ms = v < 946684800000 ? v * 1000 : v;
            return format(new Date(ms), "dd/MM/yyyy HH:mm");
        },
    },
];

/** Convert dayjs range to ms timestamps [from, to] */
function rangeToMs(range) {
    if (!range || !range[0] || !range[1]) return [null, null];
    return [
        range[0].startOf("day").valueOf(),
        range[1].endOf("day").valueOf(),
    ];
}

function BonusHistory() {
    const [data, setData]       = useState([]);
    const [total, setTotal]     = useState(0);
    const [page, setPage]       = useState(1);
    const [loading, setLoading] = useState(false);

    // Filter state
    const [userId,    setUserId]    = useState(null);
    const [type,      setType]      = useState("");
    const [dateRange, setDateRange] = useState(null);

    // Committed filter (applied on search)
    const committed = useRef({ userId: null, type: "", dateFrom: null, dateTo: null });

    const load = useCallback(async (p = 1, filters = committed.current) => {
        setLoading(true);
        try {
            const params = { page: p, limit: PAGE_SIZE };
            if (filters.userId)   params.userId   = filters.userId;
            if (filters.type)     params.type     = filters.type;
            if (filters.dateFrom) params.dateFrom = filters.dateFrom;
            if (filters.dateTo)   params.dateTo   = filters.dateTo;

            const { data: res } = await authApi.getBonusHistory(params);
            setData(res.data  || []);
            setTotal(res.total || 0);
        } catch { /* silent */ }
        finally { setLoading(false); }
    }, []);

    // Load on mount
    useEffect(() => { load(1); }, [load]);

    const onSearch = () => {
        const [dateFrom, dateTo] = rangeToMs(dateRange);
        committed.current = { userId, type, dateFrom, dateTo };
        setPage(1);
        load(1, committed.current);
    };

    const onReset = () => {
        setUserId(null);
        setType("");
        setDateRange(null);
        committed.current = { userId: null, type: "", dateFrom: null, dateTo: null };
        setPage(1);
        load(1, committed.current);
    };

    const onPageChange = (p) => {
        setPage(p);
        load(p);
    };

    return (
        <div className="flex flex-col gap-3">
            {/* Filter bar */}
            <div className="bg-surface-sunken border border-line rounded-lg p-3 flex flex-wrap items-end gap-3">
                <div className="flex flex-col gap-1">
                    <span className="text-xs text-content-3 font-medium">User ID</span>
                    <InputNumber
                        value={userId}
                        onChange={setUserId}
                        placeholder="Nhập User ID"
                        min={1}
                        style={{ width: 130 }}
                        onPressEnter={onSearch}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs text-content-3 font-medium">Loại</span>
                    <Select
                        value={type}
                        onChange={setType}
                        options={TYPE_OPTIONS}
                        style={{ width: 120 }}
                    />
                </div>

                <div className="flex flex-col gap-1">
                    <span className="text-xs text-content-3 font-medium">Khoảng thời gian</span>
                    <RangePicker
                        value={dateRange}
                        onChange={setDateRange}
                        format="DD/MM/YYYY"
                        style={{ width: 240 }}
                        placeholder={["Từ ngày", "Đến ngày"]}
                    />
                </div>

                <div className="flex gap-2">
                    <Button type="primary" onClick={onSearch}>Tìm</Button>
                    <Button onClick={onReset}>Xoá lọc</Button>
                    <ExportCsvButton
                        filename="so-thuong"
                        disabled={!total}
                        tooltip="Tải toàn bộ bản ghi đang lọc ra CSV (mọi trang) để tự cộng tổng thưởng / trừ tiền trong Excel"
                        request={() => authApi.exportBonusHistory(committed.current)}
                    />
                </div>
            </div>

            {/* Table */}
            <Table
                rowKey="bonusId"
                columns={columns}
                dataSource={data}
                loading={loading}
                size="small"
                scroll={{ x: 800 }}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    showTotal: (t) => `Tổng ${t} bản ghi`,
                    onChange: onPageChange,
                    showSizeChanger: false,
                }}
            />
        </div>
    );
}

export default BonusHistory;
